import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDragIndicator } from 'react-icons/md';
import { IconEye, IconEdit, IconTrash, IconArrowsSort } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface ClientData {
  id: number;
  name: string;
  tasks: any[];
  assign_drivers: any[];
}

interface ClientListTableProps {
  clientData: ClientData[];
  // EV-493。このページの先頭の行の前にある件数（(ページ - 1) × 1ページの件数）と、一覧全体の件数。
  positionOffset?: number;
  totalCount?: number;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
  onDeleteClick: (client: ClientData) => void;
  // EV-493。「順番を指定して移動」。position は一覧全体（絞り込み後）での1からの順番。
  onMoveToPosition?: (client: ClientData, position: number) => void;
}

const ClientListTable: React.FC<ClientListTableProps> = ({
  clientData,
  positionOffset = 0,
  totalCount,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDeleteClick,
  onMoveToPosition,
}) => {
  const { t } = useTranslation('client');
  const navigate = useNavigate();
  const total = totalCount ?? clientData.length;
  // 開いている「順番を指定して移動」の行と、入力中の順番・誤りの表示。
  const [movingId, setMovingId] = useState<number | null>(null);
  const [positionInput, setPositionInput] = useState('');
  const [positionError, setPositionError] = useState(false);
  const toggleRefs = useRef(new Map<number, HTMLButtonElement | null>());

  const openMove = (id: number, position: number) => {
    setMovingId(id);
    setPositionInput(String(position));
    setPositionError(false);
  };

  const closeMove = (id: number) => {
    setMovingId(null);
    setPositionError(false);
    // キーボードの利用者が、開いた操作の位置に戻れるようにする。
    toggleRefs.current.get(id)?.focus();
  };

  const submitMove = (event: React.FormEvent, item: ClientData) => {
    event.preventDefault();
    const trimmed = positionInput.trim();
    const position = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || position < 1 || position > total) {
      setPositionError(true);
      return;
    }
    setMovingId(null);
    setPositionError(false);
    onMoveToPosition?.(item, position);
  };

  const handleEditClick = (id: number) => {
    navigate(`/client/${id}/edit`);
  };

  const handleViewClick = (id: number) => {
    navigate(`/client/${id}/view`);
  };

  return (
    <section className="flex flex-col w-full">
      <div className="flex flex-row bg-gray-100 rounded-md p-2">
        <h6 className="flex-1 pl-4">{t('table.client_id')}</h6>
        <h6 className="flex-1">{t('table.client_name')}</h6>
        <h6 className="flex-1 text-center">{t('table.task_counts')}</h6>
        <h6 className="flex-1 text-center">{t('table.assignees')}</h6>
        <h6 className="flex-1 text-center">{t('table.action')}</h6>
      </div>
      {clientData.map((item, index) => (
        <div
          key={item.id}
          className="flex flex-row flex-wrap border-b p-2"
          draggable
          onDragStart={() => onDragStart(index)}
          onDragEnter={() => onDragEnter(index)}
          onDragEnd={onDragEnd}
        >
          <div className="flex flex-1 flex-row items-center text-gray-600 pl-4">
            <MdDragIndicator className="cursor-move mr-2" size={20} />
            <p className="flex-1">{item.id}</p>
          </div>
          <p className="flex-1">{item.name}</p>
          <p className="flex-1 text-center">
            {item.tasks.length} {t('table.task')}
          </p>
          <p className="flex-1 text-center">
            {item.assign_drivers.length} {t('table.driver')}
          </p>
          <div className="flex flex-1 justify-center items-center space-x-3 text-neutral/100 cursor-pointer">
            {onMoveToPosition && (
              <button
                type="button"
                ref={(element) => {
                  toggleRefs.current.set(item.id, element);
                }}
                className="inline-flex items-center"
                aria-label={t('ui.move-to-position')}
                title={t('ui.move-to-position')}
                aria-expanded={movingId === item.id}
                onClick={() =>
                  movingId === item.id
                    ? closeMove(item.id)
                    : openMove(item.id, positionOffset + index + 1)
                }
              >
                <IconArrowsSort stroke={1.5} size={20} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="inline-flex items-center"
              aria-label={t('ui.view', { ns: 'common' })}
              onClick={() => handleViewClick(item.id)}
            >
              <IconEye
                stroke={1.5}
                size={20}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="inline-flex items-center"
              aria-label={t('ui.edit', { ns: 'common' })}
              onClick={() => handleEditClick(item.id)}
            >
              <IconEdit
                stroke={1.5}
                size={20}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="inline-flex items-center"
              aria-label={t('ui.delete', { ns: 'common' })}
              onClick={() => onDeleteClick(item)}
            >
              <IconTrash
                stroke={1.5}
                size={20}
                aria-hidden="true"
              />
            </button>
          </div>
          {movingId === item.id && (
            <form
              // ブラウザの検証（type="number" の max）は使わず、範囲の外を画面の言語で知らせる。
              noValidate
              className="flex basis-full flex-row flex-wrap items-center justify-end gap-2 pt-2"
              onSubmit={(event) => submitMove(event, item)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  closeMove(item.id);
                }
              }}
            >
              <label htmlFor={`client-move-${item.id}`} className="text-neutral/100">
                {t('ui.move-position-label', { count: total })}
              </label>
              <input
                id={`client-move-${item.id}`}
                type="number"
                inputMode="numeric"
                min={1}
                max={total}
                autoFocus
                className="w-24 rounded-md border px-2 py-1"
                value={positionInput}
                aria-invalid={positionError}
                aria-describedby={positionError ? `client-move-error-${item.id}` : undefined}
                onChange={(event) => {
                  setPositionInput(event.target.value);
                  setPositionError(false);
                }}
              />
              <button type="submit" className="rounded-md bg-primary/300 px-3 py-1 text-white">
                {t('ui.move')}
              </button>
              <button
                type="button"
                className="rounded-md border px-3 py-1"
                onClick={() => closeMove(item.id)}
              >
                {t('ui.cancel', { ns: 'common' })}
              </button>
              {positionError && (
                <p id={`client-move-error-${item.id}`} role="alert" className="basis-full text-right text-red-600">
                  {t('ui.move-position-invalid', { count: total })}
                </p>
              )}
            </form>
          )}
        </div>
      ))}
    </section>
  );
};

export default ClientListTable;
